import React, { Children, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import {
  useElementIsOverflowing,
  OverflowDirection,
} from '../../../helpers/hooks/useElementIsOverflowing';
import { useIsTouchDevice } from '../../../helpers/hooks/useIsTouchDevice';
import { useScrollState } from '../../../helpers/hooks/useScrollState';
import { BackButton } from '../../Clickables/Controls/BackButton/BackButton';
import { ForwardButton } from '../../Clickables/Controls/ForwardButton/ForwardButton';
import { Heading, HeadingVariant } from '../../Heading/Heading';
import { Text } from '../../Text/Text';
import styles from '../Carousel.module.css';
import { onNextClicked, onPreviousClicked } from '../helpers';

import { getTotalPages } from './helpers/getTotalPages';
import { EMPTY_LIST_SIX } from '../../../helpers/emptyListsForMapping';
import ContentLoader from 'react-content-loader';

const CAROUSEL_CHILD_CLASSES = 'mr-3 sm:mr-4 snap-center md:snap-start';

interface IProps {
  title?: string;
  description?: string;
  showPageNumber?: boolean;
  children: React.ReactNode | React.ReactNode[];
  childrenClassName?: string;
  dataTestId: string;
  outerClass?: string;
  headingVariant?: HeadingVariant;
  headingAs?: string;
  showContentLoader?: boolean;
  contentLoaderKey?: string;
  contentLoaderClassName?: string;
  isLoading?: boolean;
}

function getCurrentPage(
  scrollPosition: number,
  containerRef: React.RefObject<HTMLDivElement | null>,
): number {
  return containerRef && containerRef.current
    ? Math.ceil(
        (scrollPosition + containerRef.current.clientWidth) / containerRef.current.clientWidth,
      )
    : 1;
}

export const ContinuousCarousel = ({
  title,
  description,
  showPageNumber,
  children,
  childrenClassName = 'w-col-3 sm:w-col-5 md:w-col-3 min-w-col-3 sm:min-w-col-5 md:min-w-col-3',
  dataTestId,
  outerClass,
  headingVariant = 'md',
  headingAs = 'h3',
  showContentLoader,
  contentLoaderKey,
  contentLoaderClassName = 'w-col-3 sm:w-col-5 md:w-col-3 min-w-col-3 sm:min-w-col-5 md:min-w-col-3',
  isLoading,
}: IProps) => {
  const isTouchDevice = useIsTouchDevice();
  const numberOfChildren = Children.count(children);
  const containerRef = useRef<HTMLDivElement>(null);
  const { atEnd, atStart, scrollPosition } = useScrollState(containerRef);

  // We pass the children as deps so that the hook triggers any time the children change.
  const isOverflowing = useElementIsOverflowing(containerRef, OverflowDirection.EITHER, [children]);

  const totalPages = getTotalPages(containerRef);
  const currentPage = getCurrentPage(scrollPosition, containerRef);

  return (
    <div className={twMerge(`mt-sm ${isTouchDevice ? '-mx-6 sm:-mx-11 md:mx-0' : ''}`, outerClass)}>
      <div className={`mb-6 flex justify-between sm:mb-10`}>
        <div
          className={`flex flex-col justify-center gap-6 ${isTouchDevice ? 'ml-6 sm:ml-11 md:ml-0' : ''}`}
        >
          <Heading as={headingAs} variant={headingVariant}>
            {title}
          </Heading>
          {description && (
            <Text className="sm:w-col-10 md:w-col-5 m-0" variant="md">
              {description}
            </Text>
          )}
        </div>
        {totalPages !== 1 && (
          <div className={isTouchDevice ? 'mr-6 sm:mr-11 md:mr-0' : ''}>
            <div className="flex items-center gap-1.5">
              {!isTouchDevice && showPageNumber && (
                <span className="pr-2">{`${currentPage}/${totalPages}`}</span>
              )}
              {!isTouchDevice && isOverflowing && !atStart && (
                <BackButton
                  variant="controlWhiteBg"
                  onClick={() => {
                    onPreviousClicked(containerRef);
                  }}
                />
              )}
              {!isTouchDevice && isOverflowing && atStart && (
                <BackButton
                  variant="controlWhiteBg"
                  onClick={() => {
                    onPreviousClicked(containerRef);
                  }}
                  disabled
                />
              )}
              {!isTouchDevice && isOverflowing && !atEnd && currentPage !== totalPages && (
                <ForwardButton
                  variant="controlWhiteBg"
                  onClick={() => {
                    onNextClicked(containerRef);
                  }}
                />
              )}
              {!isTouchDevice && isOverflowing && (atEnd || currentPage === totalPages) && (
                <ForwardButton
                  variant="controlWhiteBg"
                  onClick={() => {
                    onNextClicked(containerRef);
                  }}
                  disabled
                />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col">
        <div className="flex">
          <div
            ref={containerRef}
            data-testid={dataTestId}
            className={`${styles.container} x-scroll flex snap-x overflow-y-hidden scroll-smooth`}
          >
            {/* Apply 'snap-center' class to child product cards */}
            {showContentLoader && isLoading
              ? EMPTY_LIST_SIX.map(({ id }) => (
                  <div
                    key={`${contentLoaderKey}-${id}`}
                    className={`size-full ${CAROUSEL_CHILD_CLASSES}`}
                  >
                    <ContentLoader
                      uniqueKey={`${contentLoaderKey}-loader-${id}`}
                      id={`${contentLoaderKey}-loader-${id}`}
                      speed={2}
                      backgroundColor="#f3f3f3"
                      foregroundColor="#ecebeb"
                      className={twMerge(`h-64 w-96`, contentLoaderClassName)}
                    >
                      <rect x="0" y="0" width="100%" height="100%" />
                    </ContentLoader>
                  </div>
                ))
              : Children.map(children, (child, index) => {
                  const isFirstItem = index === 0;
                  const isLastItem = index === numberOfChildren - 1;
                  const firstItemClasses = isTouchDevice ? 'ml-6! sm:ml-11! md:ml-0!' : '';
                  const lastItemClasses = isTouchDevice
                    ? 'mr-6! sm:mr-11! md:mr-0!'
                    : 'mr-0! sm:mr-0! md:mr-0!';
                  return (
                    <div
                      className={twMerge(
                        `${CAROUSEL_CHILD_CLASSES} ${isFirstItem ? firstItemClasses : ''} ${isLastItem ? lastItemClasses : ''}`,
                        childrenClassName,
                      )}
                    >
                      {child}
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </div>
  );
};
